import { CSSProperties, Component, ReactNode } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, SelectProps, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { sortByProperty } from '@synergy/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import uniqid from 'uniqid';

import { PoolTypography } from '../typography/pool-typography';
import { SelectOption } from './models/select-option';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            textAlign: 'start',
            '& > div': {
                borderRadius: '4px',
            },
            '& svg': {
                top: 'calc(50% - 12px)',
                width: 24 * 2,
                height: 24,
                color: theme.palette.text.primary,
            },
        },
        selectRoot: {
            minHeight: 24,
        },
        disabled: {
            border: 'none',
            color: '#21272E',
            opacity: 0.6,
            '& > div': {
                backgroundColor: theme.palette.background.default,
                '& span': {
                    color: '#21272E',
                    opacity: 0.6,
                },
            },
        },
        disabledLabel: {
            color: '#21272E',
            opacity: 0.6,
        },
        baseWidth: {
            minWidth: '240px',
        },
        fullWidth: {
            display: 'flex',
        },
        simple: {
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '13px',

            '& svg': {
                border: 'none',
            },

            '& > div': {
                padding: theme.spacing(1.5, 4.5, 1.5, 3),
                '&:focus': {
                    borderRadius: theme.spacing(3),
                },
            },
        },
        selectSimple: {
            fontSize: '13px',
            padding: theme.spacing(1, 1.5),
        },
        additionalValue: {
            fontSize: '0.8em',
        },
    });

// Option can be generic, but is not typeable because of static constructs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OptionType = any;

interface PoolSelectPropsBase extends WithStyles<typeof styles> {
    styleType?: keyof ReturnType<typeof styles>;
    fullWidth?: boolean;
    disallowDefaultValue?: boolean;
    options: SelectOption[];
    selectStyles?: CSSProperties;
    label?: ReactNode;
    required?: boolean;
    onValueChange?: (value: OptionType) => void;
    noSorting?: boolean;
    placeholder?: string;
}

export type PoolSelectProps = PoolSelectPropsBase & SelectProps;

interface PoolSelectState {
    value: OptionType;
    options: SelectOption[];
}

type ClassToClassMap = Partial<{
    [Class in keyof ReturnType<typeof styles>]: keyof ReturnType<typeof styles>;
}>;

export const PoolSelect = withStyles(styles)(
    class PoolSelectBase extends Component<PoolSelectProps, PoolSelectState> {
        private static baseClassToSelectClassMap: ClassToClassMap = {
            simple: 'selectSimple',
        };

        public override state: PoolSelectState = {
            value: '',
            options: [],
        };

        private elementId = uniqid();

        private static getDisplayType(props: PoolSelectProps): keyof ReturnType<typeof styles> {
            return props.styleType || 'root';
        }

        private static getClassNames(props: PoolSelectProps) {
            return `${props.classes.root} ${props.classes[this.getDisplayType(props)]} ${
                props.disabled ? props.classes.disabled : ''
            } ${props.className || ''}`;
        }

        private static getSelectClassName(props: PoolSelectProps): string {
            const classKey = this.baseClassToSelectClassMap[this.getDisplayType(props)];

            return classKey ? props.classes[classKey] : props.classes.selectRoot;
        }

        private renderLabel() {
            if (this.props['label']) {
                return (
                    <InputLabel
                        htmlFor={this.elementId}
                        className={this.props['disabled'] ? this.props['classes'].disabledLabel : ''}
                    >
                        {this.props['label']}
                        {this.props['required'] && ' *'}
                    </InputLabel>
                );
            }

            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private handleChange = (event: SelectChangeEvent<any>) => {
            this.setState({ value: event.target.value });

            // HACK undefined values are not allowed, which breaks the use-case of a value that represents undefined
            // e.g. a filter option that represents no filters applied
            // allow the exact undefined string to represent undefined
            if (this.props['onValueChange'] && typeof this.props['onValueChange'] === 'function') {
                this.props['onValueChange'](event.target.value !== 'undefined' ? event.target.value : undefined);
            }
        };

        private renderEmptyValue(disallowDefaultValue: boolean) {
            if (!disallowDefaultValue) {
                return (
                    <MenuItem value="undefined">
                        <PoolTypography typography="body1" id="components.select.resetValue" />
                    </MenuItem>
                );
            }
            return null;
        }

        public override render() {
            const {
                selectStyles: _selectStyles,
                onValueChange: _onValueChange,
                noSorting: _noSorting,
                styleType,
                classes,
                disallowDefaultValue,
                placeholder,
                ...baseSelectProps
            } = this.props;

            return (
                <FormControl
                    className={this.props['fullWidth'] ? this.props['classes'].fullWidth : ''}
                    error={this.props['error']}
                >
                    {this.renderLabel()}
                    <Select
                        {...baseSelectProps}
                        displayEmpty={false}
                        value={this.state.value}
                        onChange={this.handleChange}
                        className={PoolSelectBase.getClassNames(this.props)}
                        IconComponent={ExpandMoreIcon}
                        inputProps={{ placeholder }}
                    >
                        {this.renderEmptyValue(!!disallowDefaultValue)}
                        {this.state.options.map((option, index) => (
                            <MenuItem
                                key={this.elementId + index}
                                value={option.value}
                                className={PoolSelectBase.getSelectClassName(this.props)}
                            >
                                <span>
                                    <PoolTypography
                                        component="span"
                                        typography={styleType === 'simple' ? 'body2' : 'body1'}
                                    >
                                        {option.displayValue}
                                    </PoolTypography>
                                    <br />
                                    <span className={classes.additionalValue}>{option.additionalDisplayValue}</span>
                                </span>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        public override componentDidMount() {
            if (this.props['value'] !== undefined && this.props['value'] !== null) {
                this.setState({ value: this.props['value'] });
            }
            this.updateOptions(this.props);
        }

        public override UNSAFE_componentWillReceiveProps(props: PoolSelectProps) {
            let value;

            if (props.value !== null && props.value !== undefined) {
                value = props.value;
            } else if (props.options.some(option => option.value === 'undefined')) {
                value = 'undefined';
            } else if (props.displayEmpty) {
                value = '';
            }
            if (value !== undefined) {
                this.setState({ value });
            }
            if (this.props['options'] !== props.options) {
                this.updateOptions(props);
            }
        }

        public updateOptions = (props: PoolSelectProps) => {
            this.setState({
                options: props.noSorting ? props.options : sortByProperty({ data: props.options, key: 'displayValue' }),
            });
        };
    }
);
